import { useGetScoreListQuery } from '@fiji/common/src/features/prediction/predictionApi';
import { Stack, Avatar, Typography, List, ListItem, Box } from '@mui/material';
import { CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import React, { type JSX } from 'react';
import { UPCOMING_EVENTS_DETAILS_TEST_IDS } from 'pages/UpcomingEventsDetails/testIds';

type ScoreGrade = {
    gradeName: string;
    color: string;
};

const SCORE_MODAL_PAPER_SX = {
    width: '612px !important',
    maxWidth: '612px !important',
    borderRadius: '4px',
    boxShadow:
        '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(66, 78, 84, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
} as const;

const SCORE_MODAL_GRADES: ScoreGrade[] = [
    { gradeName: 'A+', color: '#4EBA43' },
    { gradeName: 'A', color: '#55BE45' },
    { gradeName: 'A-', color: '#B7DB48' },
    { gradeName: 'B+', color: '#B7DB48' },
    { gradeName: 'B-', color: '#FFD451' },
    { gradeName: 'B', color: '#FFD451' },
    { gradeName: 'C+', color: '#F5A338' },
    { gradeName: 'C', color: '#F5A338' },
    { gradeName: 'C-', color: '#FF8046' },
    { gradeName: 'D+', color: '#FF8046' },
    { gradeName: 'D', color: '#D75B5F' },
    { gradeName: 'D-', color: '#D75B5F' },
    { gradeName: 'F', color: '#A958EE' },
];

const SCORE_ROWS = [
    { key: 'device-mode', translationKey: 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_MODE', points: 30 },
    { key: 'events-and-alarms', translationKey: 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_EVENTS_AND_ALARMS', points: 25 },
    { key: 'device-health', translationKey: 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_HEALTH_DATA', points: 20 },
    { key: 'historical-data', translationKey: 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_HISTORICAL_DATA', points: 15 },
    {
        key: 'configuration-age',
        translationKey: 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_CONFIGURATION_AGE_BEST_PRACTICES',
        points: 10,
    },
] as const;

const getScoreGrades = (scoreListData: { data?: { grades?: ScoreGrade[] } } | undefined): ScoreGrade[] =>
    scoreListData?.data?.grades?.length ? scoreListData.data.grades : SCORE_MODAL_GRADES;

const ScorePoint = ({ value }: { value: number }): JSX.Element => (
    <Box
        sx={{
            alignItems: 'center',
            border: '1px solid #B9BFC2',
            borderRadius: '4px',
            display: 'flex',
            height: 32,
            justifyContent: 'center',
            width: 30,
        }}
    >
        <Typography sx={{ color: '#424E54', fontSize: 16, lineHeight: '16px' }}>{value}</Typography>
    </Box>
);

export const ScoreModal = React.forwardRef((props: any, ref): JSX.Element => {
    const [openPulseScore, setOpenPulseScore] = React.useState(false);
    const { data: scoreListData, isLoading }: any = useGetScoreListQuery(undefined, { skip: !openPulseScore });
    const pulseScoreActions: ModalActions[] = [
        {
            key: 'ok',
            label: <CustomTransComponent translationKey="COMMON:OK" />,
            close: true,
            variant: 'contained',
            sx: { height: 36, minWidth: 68, px: 2 },
            handleClick: (): void => {
                setOpenPulseScore(false);
            },
        },
    ];

    React.useImperativeHandle(ref, () => ({
        onClick: (): void => setOpenPulseScore(true),
    }));

    return (
        <CustomModal
            isOpen={openPulseScore}
            isLoading={isLoading}
            noDividers
            hidePadding
            type="medium"
            actions={pulseScoreActions}
            paperSx={SCORE_MODAL_PAPER_SX}
            footerClass="score-modal__footer"
            header={<CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE" />}
            data-testid={props?.['data-testid'] ?? UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
            aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
        >
            <>
                <Stack
                    sx={{ px: 3 }}
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                >
                    <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ opacity: 0.8, py: 1 }}>
                        {getScoreGrades(scoreListData).map(
                            (item): JSX.Element => (
                                <Avatar
                                    key={item.gradeName + item.color}
                                    sx={{ backgroundColor: item.color, height: 40, width: 40 }}
                                    aria-label={`score-grade-${item.gradeName.toLowerCase()}`}
                                >
                                    <Typography
                                        className="text-white"
                                        fontWeight={'700'}
                                        fontSize={'20px'}
                                        textAlign={'center'}
                                    >
                                        {item.gradeName}
                                    </Typography>
                                </Avatar>
                            )
                        )}
                    </Stack>
                    <Typography
                        variant="body1"
                        className="black-500"
                        sx={{ mt: 1, fontSize: 16, lineHeight: '22px' }}
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                    >
                        <CustomTransComponent
                            translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_WEIGHTED_METHOD"
                            components={{ bold: <span className="fw-700 f-16" /> }}
                        />
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#424E54', fontSize: 16, lineHeight: '22px', mt: 1.25 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Group PulseScore™
                        </Box>{' '}
                        is based on the scores of the devices and groups within a group.
                    </Typography>
                </Stack>
                <List
                    disablePadding
                    sx={{ py: 1 }}
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                >
                    {SCORE_ROWS.map((row, index) => (
                        <ListItem
                            key={row.key}
                            sx={{
                                bgcolor: index % 2 === 1 ? '#FBFBFB' : '#FFFFFF',
                                justifyContent: 'space-between',
                                minHeight: 38,
                                px: 3,
                                py: 0,
                            }}
                        >
                            <Typography variant="body1" sx={{ color: '#424E54', fontSize: 16 }}>
                                <CustomTransComponent translationKey={row.translationKey} />
                            </Typography>
                            <ScorePoint value={row.points} />
                        </ListItem>
                    ))}
                </List>
            </>
        </CustomModal>
    );
});
